import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { deleteDoc, doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { FiAlertCircle, FiCheckCircle, FiEdit2, FiFolder, FiPrinter, FiTrash2 } from 'react-icons/fi';
import { db } from '../../lib/firebase';
import { useSessionUser } from '../../lib/permissions';
import { formatDate } from '../../lib/format';
import { normalizeLinks } from '../../lib/links';
import { archiveDocuments, storedDocuments } from '../../lib/documents';
import DocumentLinks from '../documents/DocumentLinks';
import DocumentViewer from '../documents/DocumentViewer';
import { usePatientDoctorContext } from '../PatientDoctorContext';
import { BUTTON, CARD, EmptyState, Modal, Notice, PageHeader, Panel, Pill, SIZE, SkeletonRows } from '../ui/kit';
import { croAccess, toForm, typeLabel } from './croModel';

const People = ({ label, names }) =>
  names.length ? (
    <div>
      <dt className="text-[12.5px] font-medium text-black/45">{label}</dt>
      <dd className="mb-0 ml-0 mt-1 flex flex-wrap gap-1.5">
        {names.map((name) => (
          <Pill key={name}>{name}</Pill>
        ))}
      </dd>
    </div>
  ) : null;

const CroDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSessionUser();
  const access = croAccess(user);
  const { patients, ensurePatients } = usePatientDoctorContext();
  const [cro, setCro] = useState(null);
  const [status, setStatus] = useState('loading');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    ensurePatients().catch(() => {});
    return onSnapshot(
      doc(db, 'cros', id),
      (snapshot) => {
        setCro(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null);
        setStatus(snapshot.exists() ? 'ready' : 'missing');
      },
      () => setStatus('error')
    );
  }, [id, ensurePatients]);

  const patient = useMemo(() => (cro ? patients.find((item) => item._id === cro.patientId) : null), [cro, patients]);
  const back = { to: '/cro', label: 'Comptes rendus opératoires' };

  const remove = async () => {
    setBusy(true);
    setError('');
    try {
      // Its documents go to the archives of the service's Dropbox first (never erased).
      const failed = await archiveDocuments(storedDocuments(cro.documents));
      if (failed.length) {
        await updateDoc(doc(db, 'cros', id), { documents: failed });
        throw new Error('archive');
      }
      await deleteDoc(doc(db, 'cros', id));
      navigate('/cro', { replace: true });
    } catch (err) {
      setError(err.message === 'archive' ? 'Certains documents n’ont pas pu être retirés. Réessayez.' : 'Le compte rendu n’a pas pu être supprimé.');
      setBusy(false);
    }
  };

  if (status !== 'ready' || !cro || !access.visible(cro)) {
    const hidden = status === 'ready' && cro && !access.visible(cro);
    return (
      <main className="mx-auto max-w-5xl px-[20px] pb-16 pt-8 sm:px-8 lg:px-10 lg:pt-10">
        <PageHeader back={back} title={status === 'loading' ? 'Chargement…' : 'Compte rendu introuvable'} />
        <div className={`${CARD} mt-8 ${status === 'loading' ? 'p-6' : ''}`}>
          {status === 'loading' ? (
            <SkeletonRows rows={4} />
          ) : (
            <EmptyState icon={FiAlertCircle} title={hidden ? 'Ce compte rendu appartient à une autre unité.' : 'Ce compte rendu n’existe pas ou a été supprimé.'} />
          )}
        </div>
      </main>
    );
  }

  const form = toForm(cro);
  const links = normalizeLinks(cro.dropboxLinks);
  const documents = storedDocuments(cro.documents);
  const comment = String(cro.comment || '').trim();

  return (
    <main className="mx-auto max-w-5xl px-[20px] pb-16 pt-8 sm:px-8 lg:px-10 lg:pt-10">
      <PageHeader
        back={back}
        eyebrow={`Compte rendu opératoire · ${formatDate(cro.datePatient) || 'date non précisée'}`}
        title={patient ? patient.nom : 'Dossier introuvable'}
        subtitle={
          <span className="flex flex-wrap gap-1.5">
            <Pill tone={typeLabel(cro) === 'Normal' ? 'neutral' : 'brand'}>{typeLabel(cro)}</Pill>
            {cro.service && <Pill>{cro.service}</Pill>}
            {cro.statut && <Pill tone={cro.statut === 'Validé' ? 'green' : 'amber'}>{cro.statut}</Pill>}
          </span>
        }
        actions={
          <>
            {access.canExport && (
              <button type="button" onClick={() => window.print()} className={`${BUTTON.secondary} ${SIZE.md}`}>
                <FiPrinter size={15} aria-hidden="true" />
                Imprimer
              </button>
            )}
            {access.canDelete(cro) && (
              <button type="button" onClick={() => setConfirmDelete(true)} className={`${BUTTON.danger} ${SIZE.md}`}>
                <FiTrash2 size={15} aria-hidden="true" />
                Supprimer
              </button>
            )}
            {access.canEdit(cro) && (
              <Link to={`/cro/${cro.id}/modifier`} className={`${BUTTON.primary} ${SIZE.md}`}>
                <FiEdit2 size={15} aria-hidden="true" />
                Modifier
              </Link>
            )}
          </>
        }
      />

      {location.state && location.state.saved && (
        <div className="mt-6 print:hidden">
          <Notice tone="green" icon={FiCheckCircle}>
            Compte rendu enregistré.
          </Notice>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 gap-[16px] lg:grid-cols-3">
        <Panel title="Intervention" className="lg:col-span-2">
          <dl className="m-0 flex flex-col gap-[14px]">
            <People label="Indication opératoire" names={form.indications} />
            <People label="Diagnostic" names={form.diagnostics} />
            {!form.indications.length && !form.diagnostics.length && <p className="m-0 text-[14px] text-black/45">Diagnostic et indication non renseignés.</p>}
            {comment && (
              <div>
                <dt className="text-[12.5px] font-medium text-black/45">Commentaire</dt>
                <dd className="mb-0 ml-0 mt-0.5 whitespace-pre-wrap break-words text-[14.5px] text-ink">{comment}</dd>
              </div>
            )}
          </dl>
        </Panel>
        <Panel title="Dossier patient">
          {patient ? (
            <Link to={`/patients/${patient._id}`} className="-mx-2 flex items-center gap-[12px] rounded-2xl px-2 py-2 text-ink no-underline transition hover:bg-black/[0.03] hover:text-ink">
              <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-brand-50 text-brand-600">
                <FiFolder size={18} aria-hidden="true" />
              </span>
              <span className="min-w-0">
                <span className="block truncate text-[15px] font-semibold">{patient.nom}</span>
                <span className="block text-[13px] text-black/45">{[patient.dossierNumber && `Dossier ${patient.dossierNumber}`, patient.services].filter(Boolean).join(' · ')}</span>
              </span>
            </Link>
          ) : (
            <p className="m-0 text-[14px] text-black/50">Le dossier rattaché est introuvable (supprimé ou hors de votre liste).</p>
          )}
        </Panel>
      </div>

      <Panel title="Équipe opératoire" className="mt-[16px]">
        <dl className="m-0 grid grid-cols-1 gap-[14px] sm:grid-cols-2">
          <People label="Chirurgiens" names={form.surgeons} />
          <People label="Interne / DES" names={form.interne ? [form.interne] : []} />
          <People label="Anesthésistes" names={form.anesthesiologists} />
          <People label="Responsables CEC" names={form.cecManagers} />
          <People label="Instrumentistes" names={form.instrumentalists} />
        </dl>
        {!form.surgeons.length && !form.interne && !form.anesthesiologists.length && !form.cecManagers.length && !form.instrumentalists.length && (
          <p className="m-0 text-[14px] text-black/45">Équipe non renseignée.</p>
        )}
      </Panel>

      <Panel title="Documents" className="mt-[16px]">
        {documents.length || links.length ? (
          <DocumentLinks documents={documents} links={links} onOpen={setViewing} />
        ) : (
          <p className="m-0 text-[14px] text-black/45">Aucun document joint.</p>
        )}
      </Panel>

      {viewing && (
        <DocumentViewer item={viewing} onClose={() => setViewing(null)} canReplace={access.canEdit(cro)} canDownload={access.canExport} />
      )}

      {(cro.createdBy || cro.updatedBy) && (
        <p className="mb-0 mt-[16px] text-right text-[12.5px] text-black/40">
          {cro.createdBy && `Saisi par ${cro.createdBy}`}
          {cro.updatedBy && ` · modifié par ${cro.updatedBy} le ${formatDate(cro.updatedAt)}`}
        </p>
      )}

      <Modal
        open={confirmDelete}
        title="Supprimer le compte rendu"
        onClose={() => !busy && setConfirmDelete(false)}
        footer={
          <>
            <button type="button" onClick={() => setConfirmDelete(false)} className={`${BUTTON.secondary} ${SIZE.md}`}>
              Annuler
            </button>
            <button type="button" onClick={remove} disabled={busy} className={`${BUTTON.dangerSolid} ${SIZE.md}`}>
              {busy ? 'Suppression…' : 'Supprimer définitivement'}
            </button>
          </>
        }
      >
        <p className="m-0 text-[14.5px] leading-relaxed text-black/55">
          Le compte rendu opératoire du {formatDate(cro.datePatient) || '—'} de <strong className="text-ink">{patient ? patient.nom : 'ce patient'}</strong> sera
          supprimé définitivement. Ses documents restent conservés dans les archives du Dropbox du service.
        </p>
        {error && (
          <div className="mt-[12px]">
            <Notice tone="red">{error}</Notice>
          </div>
        )}
      </Modal>
    </main>
  );
};

export default CroDetail;
